// State store des tasks RMF : listing live + action de dispatch + building_map cache.
//
// Lifecycle : start() declenche le polling /api/tasks a 5s (assez pour la UI sans flooder
// le proxy). stop() arrete proprement. fetchBuildingMap() est appele une fois au mount du
// dispatch form (les waypoints ne changent qu'au switch-world).
//
// Le dispatch lui-meme est dans dispatch() : valide cote front via validateForCategory, puis
// POST. En cas de succes, force un refetch immediat de la list pour que la nouvelle task
// apparaisse sans attendre le prochain tick de polling.

import { untrack } from 'svelte';
import {
  backendClient,
  type DispatchTaskRequest,
  type RmfBuildingMap,
  type RmfTask,
  type RmfTaskDetail
} from '$lib/api/backend-client';
import { validateForCategory } from './dispatch-validation';
import type { MapName } from './maps';

const POLL_INTERVAL_MS = 5000;

/**
 * Origine du building_map courant. Sert au diagnostic (`__tasks.buildingMapSource`) pour savoir
 * si la UI consomme la donnee upstream rmf-web (la verite backend) ou le fallback YAML local
 * (cas degraded ou /api/building_map perpetuellement 404).
 */
export type BuildingMapSource = 'local' | 'upstream' | null;

function createTasksStore() {
  let tasks = $state<readonly RmfTask[]>([]);
  let buildingMap = $state<RmfBuildingMap | null>(null);
  let buildingMapSource = $state<BuildingMapSource>(null);
  let listPhase = $state<'idle' | 'loading' | 'ready' | 'error'>('idle');
  let lastError = $state<unknown>(null);
  /** Phase dispatch : `idle` quand rien en cours, `dispatching` pendant le POST, `error` en
   *  cas d'echec (le composant affiche lastError et propose retry). */
  let dispatchPhase = $state<'idle' | 'dispatching' | 'error'>('idle');
  let dispatchError = $state<unknown>(null);

  let pollTimer: ReturnType<typeof setInterval> | undefined;
  let cancelled = false;
  // Guard "in-flight" : empeche le polling d'empiler des fetches quand le backend lag. Sans
  // ces flags, un backend lent (ex TinyRobot coince dans le lift qui bloque task_dispatcher)
  // ferait s'accumuler les requetes -> ERR_INSUFFICIENT_RESOURCES cote browser (Edge/Chrome
  // ont une limite ~6 connexions par origine, atteinte en quelques secondes a 5s de poll).
  // Cf incident 2026-05-28.
  let refreshInFlight = false;
  let refreshDetailsInFlight = false;

  // Backoff sur erreurs reseau successives : si N polls consecutifs echouent (typique :
  // browser sature qui repond ERR_INSUFFICIENT_RESOURCES en chaine), on suspend le polling
  // pour BACKOFF_MS pour laisser les connexions se liberer et permettre au dispatch utilisateur
  // de passer (autrement les nouveaux fetch dispatch echouent aussi). Reset a 0 sur succes.
  const POLL_FAILURE_THRESHOLD = 3;
  const BACKOFF_MS = 30_000;
  let consecutiveFailures = 0;
  let pollSuspendedUntil = 0;

  // Dedup les warn console : on log au plus une fois par bookingId par session. Sans ce filtre,
  // une task perpetuellement 404/error spam ~12 lignes par minute (poll 5s × 1 robot tracked).
  const warnedFailedIds = new Set<string>();
  function warnOnce(bookingId: string, err: unknown): void {
    if (warnedFailedIds.has(bookingId)) return;
    warnedFailedIds.add(bookingId);
    console.warn(`tasksStore.requestDetail(${bookingId}) failed (further failures silenced):`, err);
  }

  // Cooldown sur requestDetail : si la meme task a ete fetched dans les derniers
  // REQUEST_DETAIL_COOLDOWN_MS, on skip le fetch immediat (le polling principal s'en chargera).
  // Protege contre :
  //   - HMR Vite qui re-monte les composants -> burst de requestDetail au re-mount
  //   - Backend qui pousse `task_id=""` transitoire -> cycle release/request rapide
  //   - User qui ouvre/ferme le panel tasks rapidement
  const REQUEST_DETAIL_COOLDOWN_MS = 4_000;
  const recentlyFetchedAt = new Map<string, number>();

  // Cache du detail par bookingId. Alimente par requestDetail() : trigger d'un poll par taskId
  // demande, refresh a chaque tick du polling principal. Sert au gating des effets visuels
  // par robot (mousse cleaning ne se declenche que pendant perform_action, pas go_to_place).
  // Reactif pour que les composants consommateurs (Robot.svelte) re-render quand le detail
  // change.
  let detailById = $state<Record<string, RmfTaskDetail>>({});
  // Set des bookingId actifs cote consommateurs. Spread sur chaque tick pour refresh leur
  // detail. Non-reactif (Set mutable) : pas besoin que les composants re-derivent, c'est
  // tasksStore qui orchestre les fetch.
  const trackedDetails = new Set<string>();

  // Cache des payloads dispatched dans la session, par bookingId. Sert au "Relancer" : RMF
  // n'a pas d'API native pour rejouer une task, le pattern recommande (cf backend ticket
  // 2026-05-28 § retry) est de cacher le payload cote frontend et de le re-POST avec un
  // label `retry_of:OLD_ID`.
  //
  // Persistance dans sessionStorage : scoped tab (pas localStorage qui leakerait entre
  // sessions backend differentes), JSON-serializable parce que DispatchTaskRequest est un
  // discriminated union de types primitifs. Quota largement suffisant (50 tasks × 200 octets
  // < 1% du quota 5MB de sessionStorage).
  type CachedPayload = { fleetName: string | null; request: DispatchTaskRequest };
  const PAYLOAD_STORAGE_KEY = 'rmf:tasks:retry_payloads';
  const payloadByBooking = loadPayloadsFromStorage();

  function loadPayloadsFromStorage(): Map<string, CachedPayload> {
    if (typeof sessionStorage === 'undefined') return new Map();
    try {
      const raw = sessionStorage.getItem(PAYLOAD_STORAGE_KEY);
      if (!raw) return new Map();
      const parsed = JSON.parse(raw) as Record<string, CachedPayload>;
      return new Map(Object.entries(parsed));
    } catch (err) {
      // JSON.parse fail, ou shape change, ou autre corruption : on repart d'un cache vide
      // plutot que crash. Le user perd l'historique retry de cette tab mais l'app continue.
      console.warn('tasksStore: failed to restore retry payload cache, starting fresh', err);
      return new Map();
    }
  }

  function savePayloadsToStorage(): void {
    if (typeof sessionStorage === 'undefined') return;
    try {
      sessionStorage.setItem(
        PAYLOAD_STORAGE_KEY,
        JSON.stringify(Object.fromEntries(payloadByBooking))
      );
    } catch (err) {
      // Quota exceeded (jamais en pratique vu la taille) ou storage disable : on tolere,
      // le cache reste in-memory pour la session courante.
      console.warn('tasksStore: failed to persist retry payload cache', err);
    }
  }

  function cachePayload(bookingId: string, payload: CachedPayload): void {
    payloadByBooking.set(bookingId, payload);
    savePayloadsToStorage();
  }
  // Etats reactifs pour le bouton Cancel/Retry de chaque task : disable + spinner. Map en
  // $state (Set des bookingId en cours d'action) pour que la UI puisse re-render au toggle.
  let cancellingIds = $state<Set<string>>(new Set());
  let retryingIds = $state<Set<string>>(new Set());

  async function refreshTrackedDetails(): Promise<void> {
    // In-flight guard : si le precedent fetch est encore en cours (backend lent), on skip
    // pour ne pas saturer le pool de connexions browser. Le tick suivant retentera quand le
    // precedent sera fini.
    if (refreshDetailsInFlight) return;
    // Backoff actif : reseau plante, on a deja N echecs successifs. On laisse passer ce tick
    // pour ne pas re-saturer le browser et permettre au dispatch utilisateur de passer.
    if (Date.now() < pollSuspendedUntil) return;
    refreshDetailsInFlight = true;
    try {
      // Fetch en parallele tous les detail des tasks tracked. Skip les erreurs individuelles
      // (un detail 404 sur une task terminee ne doit pas casser les autres).
      const ids = [...trackedDetails];
      if (ids.length === 0) return;
      const results = await Promise.allSettled(ids.map((id) => backendClient.getTaskState(id)));
      if (cancelled) return;
      const next = { ...detailById };
      let changed = false;
      let anyFailed = false;
      for (let i = 0; i < ids.length; i++) {
        const r = results[i];
        if (r.status === 'fulfilled') {
          next[ids[i]] = r.value;
          changed = true;
        } else {
          anyFailed = true;
          warnOnce(ids[i], r.reason);
        }
      }
      if (changed) detailById = next;
      // Reset / increment du compteur d'echecs. Si tous reussis -> reset (sortir du backoff).
      // Si toutes echouees -> increment et eventuellement engager le backoff.
      if (!anyFailed) {
        consecutiveFailures = 0;
      } else if (!changed) {
        consecutiveFailures += 1;
        if (consecutiveFailures >= POLL_FAILURE_THRESHOLD) {
          pollSuspendedUntil = Date.now() + BACKOFF_MS;
          console.warn(
            `tasksStore: ${consecutiveFailures} echecs consecutifs, backoff ${BACKOFF_MS / 1000}s`
          );
        }
      }
    } finally {
      refreshDetailsInFlight = false;
    }
  }

  async function refresh(): Promise<void> {
    // In-flight guard : meme rationale que refreshTrackedDetails. Backend lent + poll 5s
    // empilerait les fetches et saturait le browser (ERR_INSUFFICIENT_RESOURCES).
    if (refreshInFlight) return;
    if (Date.now() < pollSuspendedUntil) return; // backoff actif
    refreshInFlight = true;
    try {
      const fetched = await backendClient.listTasks();
      if (cancelled) return;
      // Tri "plus recent en haut" : la nouvelle task dispatched apparait imm en tete du panel,
      // sans avoir a la chercher dans la liste. requestedAtMillis = timestamp envoye dans le
      // payload dispatch (cf real-backend-client.ts buildDescription). Fallback chain pour les
      // edge cases (task ancienne sans request_time, ou backend quirk) : startedAt > finishedAt
      // > 0 = bookingId comme tie-breaker stable. Spread copy pour ne pas muter le retour de
      // backendClient (qui pourrait etre une readonly array selon l'impl).
      const sorted = [...fetched].sort((a, b) => {
        const ta = a.requestedAtMillis ?? a.startedAtMillis ?? a.finishedAtMillis ?? 0;
        const tb = b.requestedAtMillis ?? b.startedAtMillis ?? b.finishedAtMillis ?? 0;
        if (tb !== ta) return tb - ta;
        // Tie-breaker stable : bookingId lexico. Evite des shuffles entre deux refresh si deux
        // tasks ont le meme timestamp (collision rare mais possible).
        return a.bookingId < b.bookingId ? -1 : a.bookingId > b.bookingId ? 1 : 0;
      });
      tasks = sorted;
      listPhase = 'ready';
      lastError = null;
    } catch (err) {
      if (cancelled) return;
      lastError = err;
      listPhase = 'error';
      console.warn('tasksStore.refresh failed', err);
    } finally {
      refreshInFlight = false;
    }
  }

  function start(): void {
    if (pollTimer) return;
    cancelled = false;
    if (listPhase === 'idle') listPhase = 'loading';
    void refresh();
    void refreshTrackedDetails();
    pollTimer = setInterval(() => {
      void refresh();
      void refreshTrackedDetails();
    }, POLL_INTERVAL_MS);
  }

  function stop(): void {
    cancelled = true;
    if (pollTimer) {
      clearInterval(pollTimer);
      pollTimer = undefined;
    }
  }

  /**
   * Charge le building_map une seule fois par session. Les waypoints ne changent pas hors
   * switch-world.
   *
   * En regime normal, sessionStore (au cold start) ou worldSwitch (au switch) appellent
   * setBuildingMap() avec le payload fetched par la readiness chain. Ce fallback fetch ne
   * s'execute qu'en cas de cache manque (DispatchForm ouvert avant que la readiness chain
   * ait fini, ou hot-reload dev qui reset le store).
   *
   * Phase 8.5 : ajout retry backoff. Sans retry, si l'user ouvre le form pendant le boot
   * rmf-web (cold start ~60-90s), le 404 unique laissait buildingMap=null definitivement et
   * la UI affichait "indisponible" jusqu'a re-open. Avec retry, on tente 5 fois sur ~30s.
   * Check le cache entre chaque tentative au cas ou la readiness chain pousse meanwhile.
   */
  async function ensureBuildingMap(): Promise<RmfBuildingMap | null> {
    if (buildingMap) return buildingMap;
    const RETRY_DELAYS_MS = [1000, 2000, 4000, 8000, 15000];
    let lastErr: unknown;
    for (let attempt = 0; attempt <= RETRY_DELAYS_MS.length; attempt++) {
      if (attempt > 0) {
        await new Promise((r) => setTimeout(r, RETRY_DELAYS_MS[attempt - 1]));
        // Re-check le cache : si la readiness chain a push meanwhile, on saute un fetch redondant.
        if (buildingMap) return buildingMap;
      }
      try {
        const fetched = await backendClient.getBuildingMap();
        untrack(() => {
          buildingMap = fetched;
          buildingMapSource = 'upstream';
        });
        return fetched;
      } catch (err) {
        lastErr = err;
        console.warn(`tasksStore.ensureBuildingMap attempt ${attempt + 1} failed:`, err);
      }
    }
    console.warn('tasksStore.ensureBuildingMap exhausted retries:', lastErr);
    return null;
  }

  /**
   * Push le building_map cote tasksStore depuis l'exterieur. Trois callers attendus :
   *   - [map]/+page.svelte au mount : payload derive du building.yaml local (fallback toujours
   *     dispo, rend le DispatchForm utilisable des l'arrivee sur la page)
   *   - sessionStore (readiness chain cold start) : payload upstream rmf-web (la source de
   *     verite, overwrite le fallback local quand disponible)
   *   - worldSwitch (readiness chain post-switch) : idem
   *
   * Overwrite ON (Phase 8.5 fix) parce que l'upstream est plus precis (coords en metres dans
   * son repere, eventuels updates dynamiques). Le no-overwrite precedent bloquait l'upstream
   * quand le fallback local etait deja installe. Idempotent : overwrite avec la meme donnee
   * est sans consequence.
   */
  function setBuildingMap(payload: RmfBuildingMap | null, source: BuildingMapSource = 'upstream'): void {
    if (!payload) return;
    // Si on a deja la version upstream, on n'accepte pas un downgrade vers local. L'upstream
    // est la source de verite ; si elle est arrivee, on garde meme si [map] mount re-pousse
    // le local apres.
    if (buildingMapSource === 'upstream' && source === 'local') return;
    // Defense Phase 8.8 : si l'upstream retourne un payload "vide" (0 waypoints), c'est qu'il
    // n'a pas reussi a parser ses propres data malgre HTTP 200. On rejette pour ne pas
    // overwrite un local qui marche. Concretement : rmf-web peut servir
    // {levels:[{vertices:[]}]} pendant un cold-start tardif ou apres un partial restart.
    if (source === 'upstream' && payload.waypoints.length === 0) {
      console.warn(
        '[tasksStore] Upstream building_map empty (0 waypoints), keeping current source:',
        buildingMapSource
      );
      return;
    }
    untrack(() => {
      buildingMap = payload;
      buildingMapSource = source;
    });
  }

  /** Reset au switch-world ou disconnect : le world change, les waypoints aussi. */
  function clearBuildingMap(): void {
    untrack(() => {
      buildingMap = null;
      buildingMapSource = null;
    });
  }

  /**
   * Pre-valide puis POST /api/tasks/dispatch_task. Force un refresh immediate apres succes
   * pour que la nouvelle task apparaisse dans le panel sans delai. Le caller (DispatchForm)
   * recoit la promise pour reagir (close form, show toast).
   *
   * Phase 8.5 : retry jusqu'a 2 fois avant de propager l'erreur (3 tentatives totales avec
   * 5s d'attente entre chacune). Budget total ~240s en pire cas (80s par fetch + 5s pause).
   * Les 4xx (validation echec cote backend Pydantic) ne sont PAS retryables car le payload
   * est constant : si le 1er fetch est 4xx, les retries auront le meme resultat. Filtre par
   * inspection du message error (heuristique) en attendant des erreurs typees backend.
   */
  async function dispatch(
    worldName: MapName,
    fleetName: string | null,
    request: DispatchTaskRequest
  ): Promise<RmfTask> {
    const validation = validateForCategory(worldName, fleetName, request);
    if (!validation.ok) {
      throw new Error(validation.error);
    }
    dispatchPhase = 'dispatching';
    dispatchError = null;
    const MAX_ATTEMPTS = 3;
    const RETRY_DELAY_MS = 5000;
    let lastErr: unknown;
    for (let attempt = 0; attempt < MAX_ATTEMPTS; attempt++) {
      if (attempt > 0) {
        await new Promise((r) => setTimeout(r, RETRY_DELAY_MS));
      }
      try {
        const result = await backendClient.dispatchTask(fleetName, request);
        // Cache le payload pour permettre un retry ulterieur (cf retry()). Indexe par
        // bookingId retourne par le backend. Persiste dans sessionStorage pour survivre aux
        // F5 (cache in-memory + persistant via cachePayload).
        cachePayload(result.bookingId, { fleetName, request });
        dispatchPhase = 'idle';
        void refresh();
        return result;
      } catch (err) {
        lastErr = err;
        const msg = err instanceof Error ? err.message : String(err);
        // 4xx non-retryable : payload invalide cote backend, les retries echoueront identiquement.
        if (/40[01234]|422|409/.test(msg)) {
          break;
        }
        console.warn(`Dispatch attempt ${attempt + 1}/${MAX_ATTEMPTS} failed:`, err);
      }
    }
    dispatchPhase = 'error';
    dispatchError = lastErr;
    throw lastErr;
  }

  function clearDispatchError(): void {
    dispatchPhase = 'idle';
    dispatchError = null;
  }

  /** Annule une task en cours cote backend (POST /api/tasks/cancel_task). Le state passera
   *  a 'canceled' au prochain refresh listTasks (5s max). Pendant l'appel, le bookingId est
   *  ajoute a `cancellingIds` pour que la UI puisse desactiver le bouton et afficher un
   *  spinner. Propage les erreurs au caller pour qu'il puisse afficher un toast. */
  async function cancel(bookingId: string): Promise<void> {
    if (cancellingIds.has(bookingId)) return; // anti-double-clic
    cancellingIds = new Set([...cancellingIds, bookingId]);
    try {
      await backendClient.cancelTask(bookingId);
      // Refresh immediat pour que le status canceled apparaisse dans la UI sans attendre
      // le prochain tick du polling.
      void refresh();
    } finally {
      const next = new Set(cancellingIds);
      next.delete(bookingId);
      cancellingIds = next;
    }
  }

  /** True si le payload original de cette task est en cache (= a ete dispatched dans cette
   *  session). Determine si le bouton Retry doit etre montre pour cette task. Une task
   *  dispatched depuis une autre tab/session/CLI n'aura pas de cache -> pas de retry possible. */
  function canRetry(bookingId: string): boolean {
    return payloadByBooking.has(bookingId);
  }

  /** Rejoue le dispatch d'une task echouee/annulee. Re-POST le payload original avec un
   *  label `retry_of:OLD_ID` pour tracabilite (cf backend ticket 2026-05-28). La nouvelle
   *  task a son propre bookingId, le cache est etendu (l'original reste accessible).
   *  Throws si pas de payload en cache (callers doivent verifier canRetry au prealable). */
  async function retry(bookingId: string): Promise<RmfTask> {
    const cached = payloadByBooking.get(bookingId);
    if (!cached) throw new Error(`No cached payload for ${bookingId}, cannot retry`);
    if (retryingIds.has(bookingId)) {
      throw new Error(`Retry already in flight for ${bookingId}`);
    }
    retryingIds = new Set([...retryingIds, bookingId]);
    try {
      const result = await backendClient.dispatchTask(cached.fleetName, cached.request, [
        `retry_of:${bookingId}`
      ]);
      // Cache aussi le payload pour la nouvelle task (permet retry-de-retry-de-retry).
      cachePayload(result.bookingId, cached);
      void refresh();
      return result;
    } finally {
      const next = new Set(retryingIds);
      next.delete(bookingId);
      retryingIds = next;
    }
  }

  return {
    get tasks() {
      return tasks;
    },
    /** Detail enrichi d'une task (phases, activePhaseCategory). null si pas encore fetched
     *  (premier tick apres requestDetail). Pour gater proprement les composants consommateurs
     *  qui doivent attendre la 1ere reponse avant de prendre une decision visuelle. */
    detail(bookingId: string): RmfTaskDetail | null {
      return detailById[bookingId] ?? null;
    },
    /** Marque un bookingId comme "tracked" : sera fetched a chaque tick du polling principal.
     *  Idempotent : ajouter une cle deja presente n'a pas d'effet. Si le polling n'est pas
     *  actif (`start()` jamais appele), un tick manuel est declenche pour fournir la 1ere
     *  reponse rapidement (sinon le consommateur attendrait POLL_INTERVAL_MS). */
    requestDetail(bookingId: string): void {
      if (!bookingId) return;
      const wasNew = !trackedDetails.has(bookingId);
      trackedDetails.add(bookingId);
      // Fetch immediat seulement si :
      //   - wasNew (id pas deja tracked dans cette session)
      //   - polling actif
      //   - pas en backoff (sequence d'echecs reseau)
      //   - pas en cooldown : meme task fetched < REQUEST_DETAIL_COOLDOWN_MS -> skip pour
      //     pas spam (cas HMR re-mount ou task_id transitoire vide).
      const lastFetchedAt = recentlyFetchedAt.get(bookingId) ?? 0;
      const inCooldown = Date.now() - lastFetchedAt < REQUEST_DETAIL_COOLDOWN_MS;
      if (wasNew && pollTimer && Date.now() >= pollSuspendedUntil && !inCooldown) {
        recentlyFetchedAt.set(bookingId, Date.now());
        void backendClient
          .getTaskState(bookingId)
          .then((d) => {
            if (cancelled) return;
            detailById = { ...detailById, [bookingId]: d };
          })
          .catch((err) => {
            // 404 attendu si la task a ete cleanup cote backend. Network failure log via
            // warnOnce pour ne pas inonder la console (cf incident 2026-05-28).
            warnOnce(bookingId, err);
          });
      }
    },
    /** Decremente le tracking d'un bookingId. Appele par un composant qui n'a plus besoin du
     *  detail (ex : robot plus lock). Le cache reste mais le polling s'arrete. */
    releaseDetail(bookingId: string): void {
      trackedDetails.delete(bookingId);
    },
    get buildingMap() {
      return buildingMap;
    },
    /** 'upstream' = donnee rmf-web, 'local' = fallback YAML local, null = pas encore charge. */
    get buildingMapSource() {
      return buildingMapSource;
    },
    get listPhase() {
      return listPhase;
    },
    get lastError() {
      return lastError;
    },
    get dispatchPhase() {
      return dispatchPhase;
    },
    get dispatchError() {
      return dispatchError;
    },
    /** True si une operation cancel est en vol pour ce bookingId. Sert au UI a desactiver
     *  le bouton et afficher un spinner. */
    isCancelling(bookingId: string): boolean {
      return cancellingIds.has(bookingId);
    },
    /** True si une operation retry est en vol pour ce bookingId. */
    isRetrying(bookingId: string): boolean {
      return retryingIds.has(bookingId);
    },
    canRetry,
    cancel,
    retry,
    start,
    stop,
    refresh,
    ensureBuildingMap,
    setBuildingMap,
    clearBuildingMap,
    dispatch,
    clearDispatchError
  };
}

export const tasksStore = createTasksStore();

// Debug helper DEV : expose le store sur window comme les autres stores (cf state-store.ts).
if (import.meta.env.DEV && typeof window !== 'undefined') {
  (window as unknown as { __tasks: typeof tasksStore }).__tasks = tasksStore;
}
